import { useMutation } from '@tanstack/react-query';

import type { NotifyCandidateMethod } from 'clients/CampaignsClient/CampaignsClient.types';

import { campaignsClient } from 'clients/CampaignsClient/CampaignsClient';
import { queryClient } from 'config/queryClient';
import { useGTM } from 'hooks/gtm/useGTM';
import { CANDIDATE_STATUS } from 'services/candidates/types';
import { EventBus } from 'services/event-bus/EventBus';
import { CandidateEvent } from 'services/event-bus/EventBus.types';
import { isAReplyMessage } from 'shared/legacy/contexts/CandidateContext/actions/useContact.utils';
import { useCurrentUserQuery } from 'shared/legacy/hooks/useCurrentUserQuery';
import { CandidateStatus } from 'types/candidate';
import { QueryKey } from 'types/query';
import { calculateCandidateStatusFromLabels } from 'utils/candidates/calculateCandidateStatusFromLabels';
import { getPageContext } from 'utils/getPageContext';
import { useUnlockCandidateMutation } from '../useUnlockCandidateMutation';
import { useFollowUpSchedule } from './hooks/useFollowUpSchedule';
import { useGetContactChannel } from './hooks/useGetContactChannel';
import { ContactCandidateParams } from './useContactCandidateMutation.types';
import { getNotifyCandidateData } from './utils/getNotifyCandidateData';
import { isCallData, isEmailData } from './utils/isChannelData';

export const useContactCandidateMutation = () => {
  const gtm = useGTM();

  const { data: currentUser } = useCurrentUserQuery();
  const contactChannel = useGetContactChannel();
  const getFollowUpSchedule = useFollowUpSchedule();
  const unlockCandidateMutation = useUnlockCandidateMutation();

  return useMutation(
    async (params: ContactCandidateParams<NotifyCandidateMethod>) => {
      if (!currentUser) {
        throw new Error('Current user not found');
      }

      // Skip unlock mutation for 'call' method with reasons since backend handles it all in one transaction
      const isCallWithLabels =
        isCallData(params.sendMethod, params.data) && params.data.newLabels && params.data.newLabels.length > 0;

      if (params.candidateStatus === CandidateStatus.New && !isCallWithLabels) {
        await unlockCandidateMutation.mutateAsync({
          campaignId: params.campaignId,
          esPersonId: params.data.esPersonId,
          status: params.candidateStatus,
          isUnlocked: true,
          reason: 'CONTACT_INFO',
        });
      }

      const campaignId = typeof params.campaignId === 'string' ? parseInt(params.campaignId) : params.campaignId;

      const channelData = await contactChannel(params.sendMethod, params);
      const notifyCandidateData = getNotifyCandidateData(params.sendMethod, {
        ...channelData,
        emailExtras: { getFollowUpSchedule },
        werknlExtras: { signOff: currentUser.mailSignOff.fullName },
      });

      if (isEmailData(params.sendMethod, params.data) && params.data.markAsContacted) {
        if (params.candidateStatus === CANDIDATE_STATUS.MATCHED) {
          return;
        }

        await campaignsClient.createOrUpdateCampaignCandidate({
          campaignId,
          esPersonId: channelData.data.esPersonId,
          status: CANDIDATE_STATUS.CONTACTED,
        });

        return;
      }

      const { data } = await campaignsClient.notifyCandidate({
        campaign_id: campaignId,
        candidate_id: channelData.data.esPersonId,
        send_method: params.sendMethod,
        data: notifyCandidateData,
      });

      return data;
    },
    {
      onSuccess: (_, params) => {
        const isReply = isAReplyMessage({
          es_person_id: params.data.esPersonId,
          status: params.candidateStatus,
        });

        if (params.sendMethod === 'linkedin_inmail') {
          queryClient.invalidateQueries([{ queryKey: [QueryKey.useLinkedinInMailCredits] }], { exact: false });
        }

        const callData = isCallData(params.sendMethod, params.data) && params.data;

        if (callData) {
          const { addedLabels, removedLabels } = calculateCandidateStatusFromLabels({
            currentStatus: params.candidateStatus,
            currentLabels: callData.previousLabels || [],
            newLabels: callData.newLabels || [],
          });

          addedLabels.forEach((label) => gtm.candidateLabelAddedFromCall(label, params.data.esPersonId));
          removedLabels.forEach((label) => gtm.candidateLabelRemovedFromCall(label));
        }

        gtm.contactCandidate(params.campaignId, params.data.esPersonId, getPageContext(), isReply);
        gtm.contactSentMessage(params.campaignId, params.data.esPersonId, params.sendMethod, getPageContext(), isReply);

        EventBus.emit(
          CandidateEvent.Contacted,
          {
            esPersonId: params.data.esPersonId,
            contactMethod: params.sendMethod,
            status: params.candidateStatus,
            activeJobBoard: params.activeJobBoard,
          },
          params.data.esPersonId,
        );
      },
      onError: (_, params) => {
        gtm.contactCandidateFailed(params.campaignId, params.data.esPersonId, params.sendMethod);
      },
    },
  );
};
